import { toast } from "sonner";
import { useContext } from "react";
import { CrmProvider, AuthMethod, CrmFormData } from "../types";
import AuthMethodSelector from "../AuthMethodSelector";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type SmartHomeAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: any;
};

const SmartHomeAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: SmartHomeAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const utils = trpc.useUtils();
  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la conexión.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (result.success) {
        const companyCode = formData.companyCode || crmConnection?.smarthomeRentals?.companyCode;
        const projectCode = formData.projectCode || crmConnection?.smarthomeRentals?.projectCode;

        if (merchantId && companyCode && projectCode) {
          await createConnection.mutateAsync({
            merchantId,
            provider: "smarthome-rentals" as CrmProvider,
            smarthomeRentals: {
              companyCode: companyCode || "",
              projectCode: projectCode || "",
            },
            status: "success",
            enabled: true,
          });
        }

        toast.success("Conexión exitosa", {
          description: "La conexión con el CRM se ha establecido correctamente.",
        });
      } else {
        toast.error("Error de conexión", {
          description: result.error || "No se pudo establecer la conexión con el CRM.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al probar la conexión.",
      });
    },
  });

  const handleTestConnection = () => {
    const companyCode = formData.companyCode || crmConnection?.smarthomeRentals?.companyCode;
    const projectCode = formData.projectCode || crmConnection?.smarthomeRentals?.projectCode;

    if (!companyCode || !projectCode) {
      toast.warning("Credenciales requeridas", {
        description:
          "Por favor ingresa el Company Code y Project Code antes de probar la conexión.",
      });
      return;
    }

    testConnection.mutate({
      provider: "smarthome-rentals" as CrmProvider,
      companyCode: companyCode || undefined,
      projectCode: projectCode || undefined,
    });
  };

  const handleAuthMethodChange = (method: AuthMethod) => {
    onFormDataChange({
      authMethod: method,
      companyCode: formData.authMethod === method ? formData.companyCode : "",
      projectCode: formData.authMethod === method ? formData.projectCode : "",
    });
  };

  const handleCompanyCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ companyCode: e.target.value });
  };

  const handleProjectCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ projectCode: e.target.value });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-700">
              Método de autenticación
            </Label>
            {hasConnection && crmConnection.status === "success" && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Conexión exitosa
              </span>
            )}
          </div>
          <AuthMethodSelector
            methods={["companyCodeProjectCode"]}
            selectedMethod={
              hasConnection && crmConnection.status === "success"
                ? "companyCodeProjectCode"
                : formData.authMethod || "companyCodeProjectCode"
            }
            onSelect={handleAuthMethodChange}
            isDisabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Company Code
          </Label>
          <Input
            type="text"
            value={
              hasConnection &&
              crmConnection.status === "success" &&
              crmConnection.smarthomeRentals?.companyCode
                ? "••••••••••••"
                : formData.companyCode || crmConnection?.smarthomeRentals?.companyCode || ""
            }
            onChange={handleCompanyCodeChange}
            placeholder="Ingresa tu Company Code"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Project Code
          </Label>
          <Input
            type="text"
            value={
              hasConnection &&
              crmConnection.status === "success" &&
              crmConnection.smarthomeRentals?.projectCode
                ? "••••••••••••"
                : formData.projectCode || crmConnection?.smarthomeRentals?.projectCode || ""
            }
            onChange={handleProjectCodeChange}
            placeholder="Ingresa tu Project Code"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>
        {!(hasConnection && crmConnection.status === "success") && (
          <Button
            variant="main"
            onClick={handleTestConnection}
            disabled={
              testConnection.isLoading ||
              (!formData.companyCode && !crmConnection?.smarthomeRentals?.companyCode) ||
              (!formData.projectCode && !crmConnection?.smarthomeRentals?.projectCode)
            }
          >
            {testConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
            Probar conexión
          </Button>
        )}
      </div>
    </div>
  );
};

export default SmartHomeAuthConfiguration;
